import { useMemo } from "preact/hooks";
import { SideMenu } from "../components/SideMenu/SideMenu.jsx";
import { DashboardPage, createDashboardLayout } from "../components/DashboardPage/DashboardPage.jsx";
import { AnalysisOverview } from "../features/analysis/AnalysisOverview.jsx";
import { MonthlyReview } from "../features/analysis/MonthlyReview.jsx";
import { AnalysisExplorer } from "../features/analysis/AnalysisExplorer.jsx";
import { AnalysisCharts } from "../features/analysis/AnalysisCharts.jsx";
import { BudgetReview } from "../features/analysis/BudgetReview.jsx";

export const defaultAnalysisSection = "overview";

export const analysisSectionItems = [
  {
    value: "overview",
    labelKey: "overview",
    descriptionKey: "spendingAndBudget"
  },
  {
    value: "monthly-review",
    labelKey: "monthlyStatement",
    descriptionKey: "currentMonthSnapshot"
  },
  {
    value: "explorer",
    labelKey: "explorer",
    descriptionKey: "dataNavigator"
  },
  {
    value: "charts",
    labelKey: "charts",
    descriptionKey: "expensesByMonth"
  },
  {
    value: "budget-review",
    labelKey: "budgetReview",
    descriptionKey: "analyzeOverruns"
  }
];

const analysisPageLayout = createDashboardLayout({
  columns: [
    { size: "fixed", value: "var(--app-side-menu-width)" },
    { size: "fill" }
  ],
  rows: [
    { size: "fill" }
  ],
  areas: [
    ["sections", "content"]
  ]
});

export function AnalysisPage({
  activeSection,
  onSectionChange,
  onNavigateToPage,
  rows,
  expenseRows,
  dateContext,
  profileSettings,
  onExplorerPresetChange,
  onChartsMetricChange,
  onBudgetReviewFilterChange,
  showAltHints = false,
  t = (key) => key
}) {
  const content = useMemo(() => {
    if (activeSection === "overview") {
      return <AnalysisOverview rows={rows} expenseRows={expenseRows} dateContext={dateContext} t={t} />;
    }

    if (activeSection === "monthly-review") {
      return (
        <MonthlyReview
          rows={rows}
          expenseRows={expenseRows}
          dateContext={dateContext}
          onNavigateToPage={onNavigateToPage}
          showAltHints={showAltHints}
          t={t}
        />
      );
    }

    if (activeSection === "explorer") {
      return (
        <AnalysisExplorer
          expenseRows={expenseRows}
          activePreset={profileSettings.analysis.explorer.preset}
          onNavigateToPage={onNavigateToPage}
          onPresetChange={onExplorerPresetChange}
          showAltHints={showAltHints}
          t={t}
        />
      );
    }

    if (activeSection === "charts") {
      return (
        <AnalysisCharts
          rows={rows}
          expenseRows={expenseRows}
          dateContext={dateContext}
          metric={profileSettings.analysis.charts.metric}
          onMetricChange={onChartsMetricChange}
          showAltHints={showAltHints}
          t={t}
        />
      );
    }

    if (activeSection === "budget-review") {
      return (
        <BudgetReview
          rows={rows}
          expenseRows={expenseRows}
          dateContext={dateContext}
          filter={profileSettings.analysis.budgetReview.filter}
          onFilterChange={onBudgetReviewFilterChange}
          onNavigateToPage={onNavigateToPage}
          showAltHints={showAltHints}
          t={t}
        />
      );
    }

    return <AnalysisOverview rows={rows} expenseRows={expenseRows} t={t} />;
  }, [
    activeSection,
    dateContext,
    expenseRows,
    onBudgetReviewFilterChange,
    onChartsMetricChange,
    onNavigateToPage,
    onExplorerPresetChange,
    profileSettings.analysis.budgetReview.filter,
    profileSettings.analysis.charts.metric,
    profileSettings.analysis.explorer.preset,
    rows,
    showAltHints,
    t
  ]);

  const sectionItems = useMemo(() => analysisSectionItems.map((item) => ({
    ...item,
    label: t(item.labelKey),
    description: t(item.descriptionKey)
  })), [t]);

  return (
    <DashboardPage
      layout={analysisPageLayout}
      slots={[
        {
          key: "sections",
          area: "sections",
          content: (
            <SideMenu
              items={sectionItems}
              value={activeSection}
              onChange={onSectionChange}
              showAltHints={showAltHints}
              focusBinding="Alt+S"
            />
          )
        },
        {
          key: "content",
          area: "content",
          content
        }
      ]}
    />
  );
}
